import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { API_URLS } from '../constants';
import { authenticatedFetch } from '../utils/api';

export interface BillingInfo {
  id?: string;
  planName: string;
  billingPeriod: 'monthly' | 'annual';
  nextBillingDate: string;
  amount: number;
  currency: string;
  status: 'active' | 'cancelled' | 'past_due';
  activePromotionCodeId?: string;
  currentDiscountAmount?: number;
}

interface Invoice {
  id: string;
  stripeInvoiceId?: string;
  date: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed' | 'cancelled';
  invoiceStatus?: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible'; // Stripe status
  description: string;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
  subtotal?: number;
  tax?: number;
  total?: number;
  attemptCount?: number;
  nextPaymentAttempt?: string;
}

interface InvoiceApiResponse {
  id: string;
  stripeInvoiceId?: string;
  billingDate: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed' | 'cancelled';
  invoiceStatus?: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  description: string;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
  subtotal?: number;
  tax?: number;
  total?: number;
  attemptCount?: number;
  nextPaymentAttempt?: string;
}

// PaymentMethod interface removed (managed via Stripe portal)

interface InvoiceSummary {
  total: number;
  paid: number;
  pending: number;
  failed: number;
  voided: number;
  uncollectible: number;
  totalAmount: number;
}

interface Plan {
  id: string;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  features: string[];
  isPopular?: boolean;
}

interface CurrentDiscount {
  promotionCodeId: string;
  code: string;
  type: 'percent_off' | 'amount_off';
  value: number;
  currency: string;
  duration: string;
  currentDiscountAmount: number;
  description: string;
}

interface DiscountValidation {
  isValid: boolean;
  discountDetails?: {
    originalPrice: number;
    discountedPrice: number;
    discountAmount: number;
    discountPercentage: number;
  };
  stripePromotionCodeId?: string;
  stripeCouponId?: string;
  errorMessage?: string;
}

const BillingPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();

  // Helper function to translate feature strings
  const translateFeature = (feature: string): string => {
    // Check if it's a translation key pattern (contains ':')
    if (feature.includes(':')) {
      const [keyPart, valuePart] = feature.split(':').map(s => s.trim());
      const translatedKey = t(keyPart);
      
      // Check if value part is also a translation key
      if (valuePart.startsWith('billingPage.features.')) {
        const translatedValue = t(valuePart);
        return `${translatedKey}: ${translatedValue}`;
      } else {
        // Value is a number or literal
        return `${translatedKey}: ${valuePart}`;
      }
    }
    
    // If it's just a translation key without ':'
    if (feature.startsWith('billingPage.features.')) {
      return t(feature);
    }
    
    // Fallback to original text
    return feature;
  };
  const navigate = useNavigate();
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoiceSummary, setInvoiceSummary] = useState<InvoiceSummary | null>(null);
  // Payment methods are managed directly in Stripe portal (no local render)
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isAnnualBilling, setIsAnnualBilling] = useState(false);
  const [currentView, setCurrentView] = useState<'billing' | 'subscription'>('billing');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  
  // Discount code states (for subscription view only)
  const [currentDiscount, setCurrentDiscount] = useState<CurrentDiscount | null>(null);
  const [appliedDiscounts] = useState<{[planId: string]: DiscountValidation}>({});

  const [plans, setPlans] = useState<Plan[]>([]);

  // Load plans from API
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const response = await authenticatedFetch(API_URLS.SUBSCRIPTION.GET_PLANS);
        if (response.ok) {
          const data = await response.json();
          setPlans(data.plans || []);
        } else {
          console.error('Failed to load plans from API');
          setPlans([]); // No fallback - force API dependency
        }
      } catch (error) {
        console.error('Error loading plans:', error);
        setPlans([]); // No fallback - force API dependency
      }
    };

    loadPlans();
  }, []);

  // Load real data from API - Re-execute when user authentication changes
  useEffect(() => {
    // Only load billing data if user is authenticated
    if (!user) {
      setIsLoading(false);
      setBillingInfo(null);
      return;
    }

    const loadBillingData = async (retryCount = 0) => {
      setIsLoading(true);
      try {
        // Loading billing data for authenticated user
        
        // Load subscription data
        const subscriptionResponse = await authenticatedFetch(API_URLS.SUBSCRIPTION.GET_USER_SUBSCRIPTION);
        if (subscriptionResponse.ok) {
          const subscriptionData = await subscriptionResponse.json();
          // Subscription data loaded successfully
          setBillingInfo({
            id: subscriptionData.subscription.id, // Add id to billingInfo
            planName: subscriptionData.subscription.planName,
            billingPeriod: subscriptionData.subscription.billingPeriod || 'monthly',
            nextBillingDate: subscriptionData.subscription.nextBillingDate,
            amount: subscriptionData.subscription.amount,
            currency: subscriptionData.subscription.currency,
            status: subscriptionData.subscription.status
          });
        } else {
          // No subscription found, user has Free plan
          // Set default Free plan info when no subscription exists
          setBillingInfo({
            id: undefined, // No ID for Free plan
            planName: 'Free',
            billingPeriod: 'monthly',
            nextBillingDate: '',
            amount: 0,
            currency: 'USD',
            status: 'active'
          });
        }

        // Load invoices data with all 17 states support
        const invoicesResponse = await authenticatedFetch(API_URLS.SUBSCRIPTION.GET_USER_INVOICES);
        if (invoicesResponse.ok) {
          const invoicesData = await invoicesResponse.json();
          setInvoices(invoicesData.invoices.map((invoice: InvoiceApiResponse) => ({
            id: invoice.id,
            stripeInvoiceId: invoice.stripeInvoiceId,
            date: invoice.billingDate,
            amount: invoice.amount,
            currency: invoice.currency,
            status: invoice.status, // Local status
            invoiceStatus: invoice.invoiceStatus, // Stripe status (17 states)
            description: invoice.description,
            hostedInvoiceUrl: invoice.hostedInvoiceUrl,
            invoicePdf: invoice.invoicePdf,
            subtotal: invoice.subtotal,
            tax: invoice.tax,
            total: invoice.total,
            attemptCount: invoice.attemptCount,
            nextPaymentAttempt: invoice.nextPaymentAttempt
          })));
          
          // Set invoice summary if available
          if (invoicesData.summary) {
            setInvoiceSummary(invoicesData.summary);
          }
        }

        // No local payment methods to render

        setIsLoading(false);
      } catch (error) {
        console.error('Error loading billing data:', error);
        
        // If we get a 401 error and this is not the 3rd retry attempt,
        // wait briefly and try again (could be an auth race condition)
        if (error instanceof Error && error.message.includes('401') && retryCount < 2) {
          // 401 error detected, retrying authentication
          // Wait 800ms before retry (increasing with each attempt)
          const delayMs = 800 + (retryCount * 400);
          setTimeout(() => loadBillingData(retryCount + 1), delayMs);
          return;
        }
        
        // Only show error message if all retries have failed
        if (retryCount >= 2) {
          setMessage({ type: 'error', text: 'Failed to load billing information' });
        }
        
        // Set default free plan if we couldn't load subscription data
        if (!billingInfo) {
          setBillingInfo({
            id: undefined,
            planName: 'Free',
            billingPeriod: 'monthly',
            nextBillingDate: '',
            amount: 0,
            currency: 'USD',
            status: 'active'
          });
        }
        
        setIsLoading(false);
      }
    };

    loadBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Re-execute when user authentication state changes

  // Load current discount information
  useEffect(() => {
    const loadCurrentDiscount = async () => {
      try {
        const response = await authenticatedFetch(API_URLS.DISCOUNTS.CURRENT);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.hasActiveDiscount) {
            setCurrentDiscount(data.discount);
          } else {
            setCurrentDiscount(null);
          }
        }
      } catch (error) {
        console.error('Error loading current discount:', error);
      }
    };

    if (user) {
      loadCurrentDiscount();
    }
  }, [user]);

  // Check URL parameters to determine initial view AND handle Stripe checkout redirect
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const viewParam = searchParams.get('view');
    const success = searchParams.get('success') === 'true';
    const canceled = searchParams.get('canceled') === 'true';
    const sessionId = searchParams.get('session_id');

    // Handle view parameter
    if (viewParam === 'subscription') {
      setCurrentView('subscription');
    } else {
      setCurrentView('billing');
    }

    // ✅ ROBUST FIX: Handle Stripe checkout redirect with deduplication
    if (success) {
      setMessage({ type: 'success', text: t('billingPage.messages.paymentSuccess') || 'Payment successful! Your subscription has been updated.' });
      
      (async () => {
        setIsLoading(true);
        try {
          // Step 1: Sync with session_id if available (backend will deduplicate)
          if (sessionId) {
            await authenticatedFetch(API_URLS.SUBSCRIPTION.SYNC_CHECKOUT_SESSION, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ sessionId }),
            });
          }

          // Step 2: Retry with backoff to handle webhook latency
          const maxRetries = 5;
          let attempt = 0;
          let subscriptionData = null;

          while (attempt < maxRetries && !subscriptionData) {
            const delay = attempt === 0 ? 500 : Math.min(1000 * Math.pow(2, attempt - 1), 6000);
            if (attempt > 0) {
              await new Promise(resolve => setTimeout(resolve, delay));
            }

            const res = await authenticatedFetch(API_URLS.SUBSCRIPTION.GET_USER_SUBSCRIPTION);
            if (res.ok) {
              const data = await res.json();
              if (data?.subscription) {
                subscriptionData = data.subscription;
                break;
              }
            }
            attempt++;
          }

          if (subscriptionData) {
            setBillingInfo(subscriptionData);
          } else {
            setMessage({ type: 'error', text: 'Subscription updated but data not loaded. Please refresh the page.' });
          }

        } catch (e) {
          console.error('Error syncing subscription:', e);
          setMessage({ type: 'error', text: 'Error loading subscription data. Please refresh the page.' });
        } finally {
          setIsLoading(false);
          navigate('/billing', { replace: true });
        }
      })();
    }

    if (canceled) {
      setMessage({ type: 'error', text: t('billingPage.messages.paymentCanceled') || 'Payment was cancelled.' });
      navigate('/billing', { replace: true });
    }

  }, [location.search, user, navigate, t]);







  const handleUpgradePlan = async (planId: string) => {
    try {
      setLoadingPlan(planId);
      setMessage(null);
      
      const plan = plans.find(p => p.id === planId);
      if (!plan) {
        setMessage({ type: 'error', text: 'Invalid plan selected' });
        return;
      }

      // ✅ BEST PRACTICE: Check if user has paid subscription
      const hasPaidSubscription = billingInfo && 
                                   billingInfo.status === 'active' && 
                                   billingInfo.planName !== 'Free';

      // If has paid subscription → Redirect to Stripe Portal (handles proration, payment, etc.)
      if (hasPaidSubscription) {
        toast.info('Redirecting to Stripe to update your subscription...');
        
        const portalResponse = await authenticatedFetch(
          API_URLS.STRIPE.CREATE_PORTAL_SESSION,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              returnUrl: `${window.location.origin}/billing`
            })
          }
        );

        if (portalResponse.ok) {
          const portalData = await portalResponse.json();
          if (portalData.url) {
            // Stripe Portal handles:
            // - Plan changes with proration preview
            // - Payment method updates
            // - Invoice history
            // - Subscription cancellation
            window.location.href = portalData.url;
            return;
          }
        }
        
        setMessage({ type: 'error', text: 'Could not open billing portal' });
        setLoadingPlan(null);
        return;
      }

      // If NO paid subscription → Checkout (to create new subscription)
      const pricesResponse = await authenticatedFetch(API_URLS.STRIPE.GET_PRICES);
      if (!pricesResponse.ok) {
        setMessage({ type: 'error', text: 'Could not load pricing information' });
        setLoadingPlan(null);
        return;
      }

      const pricesData = await pricesResponse.json();
      const priceId = pricesData.prices[planId]?.[isAnnualBilling ? 'annual' : 'monthly'];
      
      if (!priceId) {
        setMessage({ type: 'error', text: 'Price not available for this plan' });
        setLoadingPlan(null);
        return;
      }

      // Create Stripe checkout session
      const checkoutResponse = await authenticatedFetch(API_URLS.STRIPE.CREATE_CHECKOUT_SESSION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId,
          successUrl: `${window.location.origin}/billing?success=true&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/billing?canceled=true`,
          promotionCodeId: appliedDiscounts[planId]?.stripePromotionCodeId
        })
      });

      if (!checkoutResponse.ok) {
        const errorData = await checkoutResponse.json();
        const errorMessage = errorData.error?.message || errorData.message || 'Failed to create checkout session';
        setMessage({ type: 'error', text: errorMessage });
        setLoadingPlan(null);
        return;
      }

      const checkoutData = await checkoutResponse.json();
      // Redirect to Stripe Checkout
      window.location.href = checkoutData.url;

    } catch (error) {
      console.error('[UPGRADE] Error:', error);
      setMessage({ type: 'error', text: 'Failed to process subscription' });
      setLoadingPlan(null);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const response = await authenticatedFetch(API_URLS.SUBSCRIPTION.CANCEL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Subscription cancelled successfully' });
        setShowCancelModal(false);
        if (billingInfo) {
          setBillingInfo({ ...billingInfo, status: 'cancelled' });
        }
        // Reload billing data
        window.location.reload();
      } else {
        const errorData = await response.json();
        // Handle both old and new error response formats
        const errorMessage = errorData.error?.message || errorData.message || 'Failed to cancel subscription';
        setMessage({ type: 'error', text: errorMessage });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to cancel subscription' });
    }
  };

  // handleManagePaymentDetails removed; portal is opened inline where needed

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getInvoiceStatusColor = (status: string, invoiceStatus?: string) => {
    // Priority: Show Stripe status first if available, then local status
    const displayStatus = invoiceStatus || status;
    
    switch (displayStatus.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'pending':
      case 'open':
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'failed':
      case 'void':
      case 'uncollectible':
        return 'bg-red-100 text-red-800 border border-red-200';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 border border-gray-200';
      default:
        return 'bg-blue-100 text-blue-800 border border-blue-200';
    }
  };

  const getDisplayStatus = (status: string, invoiceStatus?: string) => {
    const displayStatus = invoiceStatus || status;
    const statusText = displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1);
    
    // Add status icons
    switch (displayStatus.toLowerCase()) {
      case 'paid':
        return (
          <div className="flex items-center">
            <svg className="w-3 h-3 mr-1.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {t('billingPage.subscription.paid')}
          </div>
        );
      case 'pending':
      case 'open':
      case 'draft':
        return (
          <div className="flex items-center">
            <svg className="w-3 h-3 mr-1.5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8-8a8 8 0 00-8-8 8 8 0 000 16z" clipRule="evenodd" />
            </svg>
            {statusText}
          </div>
        );
      case 'failed':
      case 'void':
      case 'uncollectible':
        return (
          <div className="flex items-center">
            <svg className="w-3 h-3 mr-1.5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {statusText}
          </div>
        );
      default:
        return statusText;
    }
  };

  const handleViewInvoice = (invoice: Invoice) => {
    if (invoice.hostedInvoiceUrl) {
      // Open Stripe hosted invoice in new tab
      window.open(invoice.hostedInvoiceUrl, '_blank');
    } else if (invoice.invoicePdf) {
      // Fallback to PDF if available
      window.open(invoice.invoicePdf, '_blank');
    } else {
      // Show invoice details in a modal or alert
      setMessage({ 
        type: 'error', 
        text: `Invoice ${invoice.id} details not available. Please contact support.` 
      });
    }
  };

  // Admin users see different message
  if (user?.role === 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-4 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white dark:bg-slate-800 shadow-sm rounded-lg border border-gray-200 dark:border-slate-700 p-6 mb-6 transition-colors duration-300">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100 mb-2 transition-colors duration-300">{t('billingPage.subscription.title')}</h1>
            <p className="text-gray-600 dark:text-slate-300 transition-colors duration-300">{t('billingPage.subscription.subtitle')}</p>
          </div>
          <div className="bg-brand-light-gray dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg p-6 transition-colors duration-300">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-brand-electric-blue" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-brand-dark-blue dark:text-slate-200 transition-colors duration-300">Admin Account</h3>
                <p className="text-sm text-brand-dark-blue dark:text-slate-300 mt-1 transition-colors duration-300">
                  No billing information is applicable for admin users.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-4 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400 dark:border-slate-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'subscription') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-4 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="bg-white dark:bg-slate-800 shadow-sm rounded-lg border border-gray-200 dark:border-slate-700 p-6 transition-colors duration-300">
            <div className="flex justify-between items-center">
                              <h2 className="text-2xl font-semibold text-gray-900 dark:text-slate-100 transition-colors duration-300">{t('billingPage.choosePlan')}</h2>
              <button
                onClick={() => {
                  setCurrentView('billing');
                  navigate('/billing');
                }}
                className="text-brand-electric-blue hover:text-brand-dark-blue dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors duration-200"
              >
{t('billingPage.backToBilling')}
              </button>
            </div>
          </div>

          {/* Success/Error Message */}
          {message && (
            <div className={`p-4 rounded-md transition-colors duration-200 ${
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-300'
            }`}>
              {message.text}
            </div>
          )}

          {/* Subscription Plan Selection Page */}
          <div className="bg-gray-100 dark:bg-slate-800 p-4 sm:p-8 font-sans rounded-lg transition-colors duration-300">
            <div className="max-w-7xl mx-auto">
              {/* Preview Release Banner */}
              <div className="bg-gradient-to-r from-brand-light-gray dark:from-slate-700 to-gray-50 dark:to-slate-600 border border-gray-200 dark:border-slate-600 rounded-xl p-8 mb-8 shadow-sm transition-colors duration-300">
                <div className="text-center">
                  <div className="flex justify-center items-center mb-4">
                    <div className="bg-brand-light-gray dark:bg-slate-700 rounded-full p-2 mr-3 transition-colors duration-300">
                      <svg className="h-5 w-5 text-brand-electric-blue" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-brand-dark-blue dark:text-slate-100 transition-colors duration-300">
                      {t('billingPage.previewRelease')}
                    </h3>
                  </div>
                  <div className="max-w-6xl mx-auto">
                    <p className="text-brand-dark-blue dark:text-slate-300 leading-relaxed text-base transition-colors duration-300">
{t('billingPage.previewReleaseDescription')}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-800 dark:text-slate-100 mb-2 transition-colors duration-300">{t('billingPage.title')}</h2>
                <p className="text-gray-600 dark:text-slate-300 transition-colors duration-300">{t('billingPage.subtitle')}</p>
              </div>
              
              {/* Billing Toggle */}
              <div className="flex justify-center items-center space-x-4 mb-8">
                <span className={`font-medium transition-colors duration-200 ${!isAnnualBilling ? 'text-gray-700 dark:text-slate-200' : 'text-gray-500 dark:text-slate-400'}`}>{t('billingPage.monthly')}</span>
                <label className="flex items-center cursor-pointer" htmlFor="billing-toggle">
                  <div className="relative">
                    <input 
                      id="billing-toggle"
                      type="checkbox" 
                      className="sr-only focus-visible:ring-2 focus-visible:ring-brand-electric-blue focus-visible:ring-offset-2"
                      checked={isAnnualBilling}
                      onChange={(e) => setIsAnnualBilling(e.target.checked)}
                      aria-label="Toggle between monthly and annual billing"
                    />
                    <div className={`block w-14 h-8 rounded-full transition-colors duration-300 ${
                      isAnnualBilling ? 'bg-brand-electric-blue' : 'bg-gray-300 dark:bg-slate-600'
                    }`}>
                      <div className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform duration-300 ease-in-out shadow-sm ${
                        isAnnualBilling ? 'transform translate-x-6' : ''
                      }`}></div>
                    </div>
                  </div>
                </label>
                <span className={`font-medium transition-colors duration-200 ${isAnnualBilling ? 'text-brand-electric-blue dark:text-blue-400' : 'text-gray-500 dark:text-slate-400'}`}>
{t('billingPage.annual')} ({t('billingPage.savePercent')})
                </span>
              </div>

              {/* Discount Code Section */}


              {/* Plan Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 items-stretch">
                {plans.map((plan) => (
                  <div 
                    key={plan.id}
                    className={`bg-white dark:bg-slate-800 rounded-lg shadow-md border flex flex-col h-full relative min-h-[480px] sm:min-h-[520px] md:min-h-[560px] transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-xl ${
                      plan.isPopular ? 'border-2 border-brand-electric-blue shadow-lg dark:shadow-lg dark:shadow-blue-500/20' : 'border-gray-200 dark:border-slate-700'
                    }`}
                  >
                    {plan.isPopular && (
                      <span className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-brand-electric-blue text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
{t('billingPage.mostPopular')}
                      </span>
                    )}
                    <div className="p-4 sm:p-6 flex flex-col h-full text-left">
                      <h3 className={`text-base sm:text-lg font-semibold mb-3 sm:mb-4 transition-colors duration-300 ${plan.isPopular ? 'text-brand-dark-blue dark:text-blue-300' : 'text-gray-800 dark:text-slate-100'}`}>
                        {plan.name}
                      </h3>
                      <div className="mb-4 sm:mb-6 text-left">
                        {(() => {
                          const originalPrice = isAnnualBilling ? plan.annualPrice : plan.monthlyPrice;
                          const appliedDiscount = appliedDiscounts[plan.id];
                          const hasDiscount = appliedDiscount?.isValid && appliedDiscount?.discountDetails;
                          
                          if (hasDiscount && plan.id !== 'free') {
                            const discountedPrice = appliedDiscount.discountDetails!.discountedPrice;
                            const savings = originalPrice - discountedPrice;
                            
                            return (
                              <div>
                                <p className="text-3xl font-bold text-gray-900 dark:text-slate-100 transition-colors duration-300">
                                  {formatCurrency(discountedPrice, 'USD')}
                                  <span className="text-base font-normal text-gray-500 dark:text-slate-400 ml-1 transition-colors duration-300">
                                    /{isAnnualBilling ? 'year' : 'month'}
                                  </span>
                                </p>
                                <div className="flex items-center mt-2">
                                  <p className="text-lg text-gray-500 dark:text-slate-400 line-through mr-2 transition-colors duration-300">
                                    {formatCurrency(originalPrice, 'USD')}
                                  </p>
                                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs font-medium rounded-full transition-colors duration-300">
                                    Save {formatCurrency(savings, 'USD')}
                                  </span>
                                </div>
                              </div>
                            );
                          } else {
                            return (
                              <p className="text-3xl font-bold text-gray-900 dark:text-slate-100 transition-colors duration-300">
                                {formatCurrency(originalPrice, 'USD')}
                                <span className="text-base font-normal text-gray-500 dark:text-slate-400 ml-1 transition-colors duration-300">
                                  /{isAnnualBilling ? 'year' : 'month'}
                                </span>
                              </p>
                            );
                          }
                        })()}
                      </div>
                      <ul className="space-y-3 text-gray-600 dark:text-slate-300 flex-grow mb-6 transition-colors duration-300">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-start">
                            <span className="text-green-500 dark:text-green-400 mr-2 mt-0.5 flex-shrink-0 transition-colors duration-300">✓</span>
                            <span className="text-sm">{translateFeature(feature)}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-auto">
                        {plan.id === 'free' ? (
                          <button 
                            disabled
                            className="w-full bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-slate-200 font-semibold py-3 px-4 rounded-lg cursor-not-allowed text-left transition-colors duration-300"
                            title="Included"
                          >
                            Included
                          </button>
                        ) : (
                          <button 
                            onClick={() => handleUpgradePlan(plan.id)}
                            disabled={loadingPlan === plan.id}
                            className={`w-full font-semibold py-3 px-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 text-left ${
                              loadingPlan === plan.id
                                ? 'bg-gray-400 dark:bg-slate-600 text-white dark:text-slate-200 cursor-not-allowed opacity-60'
                                : plan.isPopular 
                                  ? 'bg-gradient-to-r from-brand-dark-blue to-brand-electric-blue dark:from-blue-600 dark:to-blue-400 text-white hover:from-brand-electric-blue hover:to-brand-dark-blue dark:hover:from-blue-500 dark:hover:to-blue-600 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 focus:ring-brand-electric-blue'
                                  : 'bg-white dark:bg-slate-700 border-2 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600 hover:border-brand-electric-blue dark:hover:border-blue-400 focus:ring-brand-electric-blue'
                            }`}
                          >
                            {loadingPlan === plan.id ? (
                              <span className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white dark:text-slate-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Processing...
                              </span>
                            ) : (
                              plan.id === 'free' ? t('billingPage.freeIncluded') : 
                               plan.id === 'starter' ? t('billingPage.chooseBasic') :
                               plan.id === 'pro' ? t('billingPage.choosePremium') :
                               t('billingPage.chooseExpert')
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Billing Management Page (Default View)
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-4 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-slate-800 shadow-sm rounded-lg border border-gray-200 dark:border-slate-700 p-6 mb-6 transition-colors duration-300">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100 mb-2 transition-colors duration-300">{t('billingPage.subscription.title')}</h1>
          <p className="text-gray-600 dark:text-slate-300 transition-colors duration-300">{t('billingPage.subscription.subtitle')}</p>
        </div>

        {/* Success/Error Message */}
        {message && (
          <div className={`p-4 rounded-md mb-6 transition-colors duration-200 ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-300'
          }`}>
            {message.text}
          </div>
        )}

        {/* Current Plan Card */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-8 border border-gray-200 dark:border-slate-700 mb-6 transition-colors duration-300">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-slate-100 mb-6 transition-colors duration-300">{t('billingPage.subscription.title')}</h2>
          
          <div className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg p-6 mb-6 transition-colors duration-300">
            <h3 className="font-semibold text-gray-700 dark:text-slate-200 mb-4 transition-colors duration-300">{t('billingPage.subscription.currentPlan')}</h3>
            <div className="flex justify-between items-center mb-2">
              <p className="text-gray-600 dark:text-slate-300 transition-colors duration-300">{t('billingPage.subscription.currentPlan')}</p>
              <p className="font-medium text-gray-800 dark:text-slate-100 transition-colors duration-300">
                {(() => {
                  const name = billingInfo?.planName || 'Free';
                  const period = billingInfo?.billingPeriod === 'annual' ? 'Annual' : 'Monthly';
                  const lower = name.toLowerCase();
                  return lower.includes('monthly') || lower.includes('annual') ? name : `${name} ${period}`;
                })()}
              </p>
            </div>
            <div className="flex justify-between items-center mb-2">
              <p className="text-gray-600 dark:text-slate-300 transition-colors duration-300">{t('billingPage.subscription.status')}</p>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full transition-colors duration-300 ${
                billingInfo?.status === 'active' 
                  ? 'text-green-800 dark:text-green-300 bg-green-100 dark:bg-green-900/30' 
                  : billingInfo?.status === 'cancelled'
                  ? 'text-red-800 dark:text-red-300 bg-red-100 dark:bg-red-900/30'
                  : 'text-yellow-800 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30'
              }`}>
{billingInfo?.status === 'active' ? t('billingPage.subscription.active') : billingInfo?.status === 'cancelled' ? 'Cancelled' : 'Past Due'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <p className="text-gray-600 dark:text-slate-300 transition-colors duration-300">{t('billingPage.subscription.nextPayment')}</p>
              <p className="font-medium text-gray-800 dark:text-slate-100 transition-colors duration-300">
                {billingInfo?.nextBillingDate 
                  ? `${formatCurrency(billingInfo.amount, billingInfo.currency)} on ${formatDate(billingInfo.nextBillingDate)}`
                  : t('billingPage.subscription.noUpcomingPayments')
                }
              </p>
            </div>
            
            {/* Current Discount Display */}
            {currentDiscount && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-600">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-gray-600 dark:text-slate-300 transition-colors duration-300">Active Discount</p>
                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs font-medium rounded-full transition-colors duration-300">
                    {currentDiscount.description}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-gray-600 dark:text-slate-300 transition-colors duration-300">Discount Amount</p>
                  <p className="font-medium text-green-600 dark:text-green-400 transition-colors duration-300">
                    -{formatCurrency(currentDiscount.currentDiscountAmount, currentDiscount.currency)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Management Buttons - Different UI for Free vs Paid Plans */}
          {(() => {
            // Determine if upgrade button should be shown
            return !billingInfo || billingInfo.planName === 'Free' || !billingInfo.status || billingInfo.status === 'cancelled' ? (
              // Free Plan - Show prominent Upgrade button
              <div>
                <h3 className="font-semibold text-gray-700 dark:text-slate-200 mb-4 transition-colors duration-300">Upgrade your plan</h3>
                <div className="bg-gradient-to-r from-brand-light-gray dark:from-slate-700 to-gray-50 dark:to-slate-600 border border-gray-200 dark:border-slate-600 rounded-lg p-6 mb-4 transition-colors duration-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-2 transition-colors duration-300">Ready to unlock more features?</h4>
                      <p className="text-gray-600 dark:text-slate-300 text-sm mb-4 transition-colors duration-300">
                        Upgrade to a paid plan and get access to advanced features, priority support, and more.
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => window.open('/billing?view=subscription', '_blank')}
                    className="w-full bg-gradient-to-r from-brand-dark-blue dark:from-blue-600 to-brand-electric-blue dark:to-blue-400 text-white font-semibold py-3 px-6 rounded-lg hover:from-brand-electric-blue dark:hover:from-blue-500 hover:to-brand-dark-blue dark:hover:to-blue-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                  >
                    🚀 Upgrade Now
                  </button>
                </div>
                
                {/* Secondary actions for free users */}
                <div className="space-y-2">
                  <button 
                    onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
                    className="w-full text-left bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 font-medium py-2 px-4 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors duration-300 text-sm"
                  >
                    View Billing History
                  </button>
                </div>
              </div>
            ) : (
              // Paid Plan - Show standard management options
              <div>
                <h3 className="font-semibold text-gray-700 dark:text-slate-200 mb-4 transition-colors duration-300">{t('billingPage.subscription.manageSubscription')}</h3>
                <div className="space-y-3">
                  <button 
                    onClick={() => setCurrentView('subscription')}
                    className="w-full text-left bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 font-semibold py-3 px-4 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors duration-300"
                  >
{t('billingPage.subscription.changePlan')}
                  </button>
                  <button 
                    onClick={async () => {
                      try {
                        const response = await authenticatedFetch(API_URLS.STRIPE.CREATE_PORTAL_SESSION, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({
                            returnUrl: `${window.location.origin}/billing`
                          })
                        });
                        if (response.ok) {
                          const data = await response.json();
                          window.open(data.url, '_blank');
                        } else {
                          setMessage({ 
                            type: 'error', 
                            text: 'Payment portal not available. Please contact support.' 
                          });
                        }
                      } catch (error) {
                        setMessage({ 
                          type: 'error', 
                          text: 'Failed to open payment portal' 
                        });
                      }
                    }}
                    className="w-full text-left bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 font-semibold py-3 px-4 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors duration-300"
                  >
{t('billingPage.subscription.managePaymentDetails')}
                  </button>
                  <button 
                    onClick={() => setShowCancelModal(true)}
                    className="w-full text-left text-red-600 dark:text-red-400 font-semibold py-3 px-4 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-300"
                  >
{t('billingPage.subscription.cancelSubscription')}
                  </button>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Payment Methods removed - handled via Stripe Portal */}

        {/* Billing History */}
        <div className="bg-white dark:bg-slate-800 shadow-sm rounded-lg border border-gray-200 dark:border-slate-700 p-6 transition-colors duration-300">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 transition-colors duration-300">{t('billingPage.subscription.billingHistory')}</h2>
            {invoiceSummary && (
              <p className="text-sm text-gray-600 dark:text-slate-300 mt-1 transition-colors duration-300">
{invoiceSummary.total} {t('billingPage.subscription.invoices')} • {t('billingPage.subscription.totalPaid')}: {invoiceSummary.totalAmount && !isNaN(invoiceSummary.totalAmount) ? formatCurrency(invoiceSummary.totalAmount, 'USD') : '$0.00'}
              </p>
            )}
          </div>
          

          
          {invoices.length > 0 ? (
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700">
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle sm:px-0">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                  <thead className="bg-gradient-to-r from-gray-50 dark:from-slate-700 to-gray-100 dark:to-slate-600 transition-colors duration-300">
                    <tr>
                      <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider transition-colors duration-300">
                        {t('billingPage.subscription.date')}
                      </th>
                      <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider hidden sm:table-cell transition-colors duration-300">
                        {t('billingPage.subscription.description')}
                      </th>
                      <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider transition-colors duration-300">
                        {t('billingPage.subscription.amount')}
                      </th>
                      <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider hidden md:table-cell transition-colors duration-300">
                        {t('billingPage.subscription.status')}
                      </th>
                      <th className="px-3 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider transition-colors duration-300">
                        {t('billingPage.subscription.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-100 dark:divide-slate-700 transition-colors duration-300">
                    {invoices.map((invoice, index) => (
                      <tr key={invoice.id} className={`hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors duration-150 ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-700/50'}`}>
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-2 sm:mr-3 transition-colors duration-300">
                              <svg className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div>
                              <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-slate-100 transition-colors duration-300">{formatDate(invoice.date)}</div>
                              <div className="text-xs text-gray-500 dark:text-slate-400 transition-colors duration-300">#{invoice.id.substring(0, 8)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 hidden sm:table-cell">
                          <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-slate-100 transition-colors duration-300">{invoice.description}</div>
                          {invoice.stripeInvoiceId && (
                            <div className="text-xs text-gray-500 dark:text-slate-400 mt-1 transition-colors duration-300">
                              Stripe: {invoice.stripeInvoiceId.substring(0, 8)}...
                            </div>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <div className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-slate-100 transition-colors duration-300">
                            {formatCurrency(invoice.amount, invoice.currency)}
                          </div>
                          {invoice.subtotal && invoice.tax && (
                            <div className="text-xs text-gray-500 dark:text-slate-400 hidden sm:block transition-colors duration-300">
                              Subtotal: {formatCurrency(invoice.subtotal, invoice.currency)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4 hidden md:table-cell">
                          <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium transition-colors duration-300 ${getInvoiceStatusColor(invoice.status, invoice.invoiceStatus)}`}>
                            {getDisplayStatus(invoice.status, invoice.invoiceStatus)}
                          </span>
                          {invoice.nextPaymentAttempt && (
                            <div className="text-xs text-gray-500 dark:text-slate-400 mt-1 transition-colors duration-300">
                              Next: {formatDate(invoice.nextPaymentAttempt)}
                            </div>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-3 sm:py-4">
                          <button 
                            onClick={() => handleViewInvoice(invoice)}
                            disabled={!invoice.hostedInvoiceUrl && !invoice.invoicePdf}
                            className={`inline-flex items-center px-2 sm:px-3 py-1.5 sm:py-2 border border-transparent text-xs sm:text-sm leading-4 font-medium rounded-md transition-colors duration-150 ${
                              invoice.hostedInvoiceUrl || invoice.invoicePdf
                                ? 'text-brand-electric-blue dark:text-blue-400 bg-brand-electric-blue dark:bg-blue-900/30 bg-opacity-10 dark:bg-opacity-30 hover:bg-opacity-20 dark:hover:bg-opacity-40 hover:text-brand-dark-blue dark:hover:text-blue-300'
                                : 'text-gray-400 dark:text-slate-500 bg-gray-100 dark:bg-slate-700 cursor-not-allowed'
                            }`}
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span className="hidden sm:inline">{t('billingPage.subscription.view')}</span>
                            <span className="sm:hidden">View</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-4 transition-colors duration-300">
                <svg className="w-8 h-8 text-gray-400 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2 transition-colors duration-300">No invoices yet</h3>
              <p className="text-gray-500 dark:text-slate-400 transition-colors duration-300">Your billing history will appear here once you have invoices.</p>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-gray-600 dark:bg-black bg-opacity-50 dark:bg-opacity-70 overflow-y-auto h-full w-full z-50 transition-colors duration-300">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-slate-800 transition-colors duration-300">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-4 transition-colors duration-300">Cancel Subscription</h3>
              <p className="text-gray-600 dark:text-slate-300 mb-4 transition-colors duration-300">
                Your plan remains active until the end of the billing period.
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="w-full bg-gray-200 dark:bg-slate-700 text-gray-800 dark:text-slate-100 font-semibold py-2 px-4 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors duration-300"
                >
                  Keep Plan
                </button>
                <button
                  onClick={handleCancelSubscription}
                  className="w-full bg-red-600 dark:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg hover:bg-red-700 dark:hover:bg-red-800 transition-colors duration-300"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
